import React from "react";
import axios from "axios";



export default class VideoPinEditor extends React.Component {
  static defaultProps = { maxDurationSec: 25 };

  constructor(props) {
    super(props);
    this.state = {
      duration: 0,
      currentTime: 0,
      isPlaying: false,

      pins: [],                // [{ id, time, role: "start"|"end" }]
      hoverTime: null,

      segments: [],            // [{ id, start, end, text }]
      activeSegmentId: null,   // id of the focused segment
      segmentText: "",         // editor content for active segment

      pendingStartIndex: null, // index in pins[] for the temporary "start"
      uiMsg: null,             // small hint message
    };

    this.videoRef = React.createRef();
    this.trackRef = React.createRef();

    this.dragging = null;

    this.onTime = this.onTime.bind(this);
    this.onLoaded = this.onLoaded.bind(this);
    this.togglePlay = this.togglePlay.bind(this);
    this.seekTo = this.seekTo.bind(this);

    this.onTrackClick = this.onTrackClick.bind(this);
    this.onTrackMove = this.onTrackMove.bind(this);
    this.onTrackLeave = this.onTrackLeave.bind(this);
    this.onTrackKeyDown = this.onTrackKeyDown.bind(this);

    this.onPinClick = this.onPinClick.bind(this);

    this.onPinDragStart = this.onPinDragStart.bind(this);
    this.onPinDrag = this.onPinDrag.bind(this);
    this.onPinDragEnd = this.onPinDragEnd.bind(this);
  }

  componentDidMount() {
    const v = this.videoRef.current;
    if (v) {
      v.addEventListener("timeupdate", this.onTime);
      v.addEventListener("loadedmetadata", this.onLoaded);
    }
  }

  componentWillUnmount() {
    const v = this.videoRef.current;
    if (v) {
      v.removeEventListener("timeupdate", this.onTime);
      v.removeEventListener("loadedmetadata", this.onLoaded);
    }
  }

  // --- video/time ---
  onTime() {
    const v = this.videoRef.current;
    if (!v) return;
    const t = Math.min(v.currentTime || 0, this.effectiveDuration());
    this.setState({ currentTime: t });
  }

  onLoaded() {
    const v = this.videoRef.current;
    if (!v) return;
    this.setState({ duration: v.duration || 0 });
  }

  togglePlay() {
    const v = this.videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      this.setState({ isPlaying: true });
    } else {
      v.pause();
      this.setState({ isPlaying: false });
    }
  }

  seekTo(t) {
    const v = this.videoRef.current;
    if (!v) return;
    const d = this.effectiveDuration();
    const clamped = Math.max(0, Math.min(t, d));
    v.currentTime = clamped;
    this.setState({ currentTime: clamped });
  }

  effectiveDuration() {
    const d = this.state.duration || 0;
    const max = this.props.maxDurationSec || d;
    if (!d) return max;
    return Math.min(d, max);
  }

  timeToPct(t) {
    const d = this.effectiveDuration();
    return d > 0 ? (t / d) * 100 : 0;
  }
  pctToTime(pct) {
    return this.effectiveDuration() * Math.min(1, Math.max(0, pct));
  }

  // --- helpers ---
  overlapsAny = (start, end, ignoreId = null) => {
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    return this.state.segments.some((s) => {
      if (s.id === ignoreId) return false;
      const slo = Math.min(s.start, s.end);
      const shi = Math.max(s.start, s.end);
      return Math.max(slo, lo) < Math.min(shi, hi);
    });
  };

  isInsideAnySegment = (t) => {
    return this.state.segments.some((s) => {
      const lo = Math.min(s.start, s.end);
      const hi = Math.max(s.start, s.end);
      return t >= lo && t <= hi;
    });
  };

  findSegmentAt = (t) => {
    const hit = this.state.segments.find((s) => {
      const lo = Math.min(s.start, s.end);
      const hi = Math.max(s.start, s.end);
      return t >= lo && t <= hi;
    });
    return hit ? hit.id : null;
  };

  // --- transcription API ---
  async requestTranscriptionForSegment({ start, end }) {
    try {
      const { data } = await axios.post("/api/transcribe-segment", {
        url: this.props.src,
        start,
        end,
      });
      return data.text || "";
    } catch (err) {
      console.error("Transcription failed", err);
      return "";
    }
  }

  // --- timeline interactions ---
  onTrackMove(e) {
    if (!this.trackRef.current) return;
    const rect = this.trackRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const t = this.pctToTime(pct);
    const clamped = Math.max(0, Math.min(this.effectiveDuration(), t));
    this.setState({ hoverTime: clamped, uiMsg: null });
  }
  onTrackLeave() {
    this.setState({ hoverTime: null });
  }

  async onTrackClick(e) {
    if (!this.trackRef.current) return;

    const rect = this.trackRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const pickedTime = Math.min(
      this.effectiveDuration(),
      Math.max(0, pct * this.effectiveDuration())
    );

    if (this.state.pendingStartIndex === null) {
      const hitId = this.findSegmentAt(pickedTime);
      if (hitId) {
        const seg = this.state.segments.find((s) => s.id === hitId);
        this.setState({
          activeSegmentId: hitId,
          segmentText: seg?.text || "",
          uiMsg: null,
        });
        return;
      }
    }

    this.seekTo(pickedTime);

    const id = `pin_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    if (this.state.pendingStartIndex === null) {
      if (this.isInsideAnySegment(pickedTime)) {
        this.setState({ uiMsg: "That area is already part of another segment." });
        return;
      }
      this.setState((s) => {
        const pins = [...s.pins, { id, time: pickedTime, role: "start" }].sort(
          (a, b) => a.time - b.time
        );
        const idx = pins.findIndex((p) => p.id === id);
        return { pins, pendingStartIndex: idx, uiMsg: null };
      });
      return;
    }

    this.setState(
      (s) => {
        const pins = [...s.pins, { id, time: pickedTime, role: "end" }].sort(
          (a, b) => a.time - b.time
        );

        const endIdx = pins.findIndex((p) => p.id === id);
        let startIdx = s.pendingStartIndex;
        if (!pins[startIdx] || pins[startIdx].role !== "start") {
          startIdx = pins.findIndex((p) => p.role === "start" && p.id !== id);
        }

        const startTime = pins[startIdx].time;
        let endTime = pins[endIdx].time;

        let sTime = Math.min(startTime, endTime);
        let eTime = Math.max(startTime, endTime);

        if (this.overlapsAny(sTime, eTime)) {
          pins.splice(endIdx, 1);
          return {
            pins,
            pendingStartIndex: s.pendingStartIndex,
            uiMsg: "New segment would overlap another segment.",
          };
        }

        const segId = `seg_${Date.now()}`;
        const newSegment = { id: segId, start: sTime, end: eTime, text: "" };

        return {
          pins,
          pendingStartIndex: null,
          segments: [...s.segments, newSegment],
          activeSegmentId: segId,
          segmentText: "",
          uiMsg: null,
        };
      },
      async () => {
        const { activeSegmentId, segments } = this.state;
        const seg = segments.find((x) => x.id === activeSegmentId);
        if (seg) {
          const text = await this.requestTranscriptionForSegment({
            start: seg.start,
            end: seg.end,
          });
          this.setState((s) => ({
            segments: s.segments.map((x) =>
              x.id === seg.id ? { ...x, text } : x
            ),
            segmentText: text,
          }));
        }
      }
    );
  }

  onTrackKeyDown(e) {
    const { currentTime } = this.state;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.seekTo(currentTime - 0.5);
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      this.seekTo(currentTime + 0.5);
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const track = this.trackRef.current;
      if (!track) return;
      const d = this.effectiveDuration();
      const pct = d > 0 ? currentTime / d : 0;
      const rect = track.getBoundingClientRect();
      const fake = { clientX: rect.left + pct * rect.width };
      this.onTrackClick(fake);
    }
  }

  onPinClick(e, segId, role) {
    e.stopPropagation();
    const seg = this.state.segments.find((s) => s.id === segId);
    this.setState({
      activeSegmentId: segId,
      segmentText: seg?.text || "",
      uiMsg: null,
    });
  }

  // --- draggable pins ---
  onPinDragStart(e, segId, role) {
    e.dataTransfer.effectAllowed = "move";
    this.dragging = { segId, role };
  }

  onPinDrag(e, segId, role) {
    if (!this.dragging || !this.trackRef.current || e.clientX <= 0) return;
    const rect = this.trackRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const newTime = this.pctToTime(pct);

    this.setState((s) => ({
      segments: s.segments.map((seg) =>
        seg.id === segId
          ? { ...seg, [role]: Math.max(0, Math.min(this.effectiveDuration(), newTime)) }
          : seg
      ),
    }));
  }

  onPinDragEnd(e, segId, role) {
    this.dragging = null;
    const seg = this.state.segments.find((s) => s.id === segId);
    if (seg && seg.id === this.state.activeSegmentId) {
      this.requestTranscriptionForSegment(seg).then((text) => {
        this.setState((s) => ({
          segmentText: text,
          segments: s.segments.map((x) =>
            x.id === seg.id ? { ...x, text } : x
          ),
        }));
      });
    }
  }

  format(t) {
    const mm = Math.floor(t / 60);
    const ss = Math.floor(t % 60);
    const cs = Math.floor((t % 1) * 100);
    return `${mm}:${String(ss).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
  }

  render() {
    const { src, className } = this.props;
    const {
      currentTime,
      isPlaying,
      pins,
      hoverTime,
      segments,
      activeSegmentId,
      segmentText,
      uiMsg,
    } = this.state;

    const d = this.effectiveDuration();
    const sortedPins = pins.slice().sort((a, b) => a.time - b.time);

    return (
      <div className={`vpe-container ${className || ""}`}>
        <div className="vpe-layout vpe-layout--videoLeft">
          <section className="vpe-main">
            <div className="vpe-videoFrame">
              <div className="vpe-videoCrop">
                <video
                  ref={this.videoRef}
                  src={src}
                  className="vpe-video"
                  controls={false}
                  preload="metadata"
                  playsInline
                  onClick={this.togglePlay}
                />
              </div>
            </div>

            <div className="vpe-controls">
              <button type="button" onClick={this.togglePlay} className="vpe-button">
                {isPlaying ? "Pause" : "Play"}
              </button>
              <div className="vpe-time">
                {this.format(currentTime)} / {this.format(d)}
              </div>
              {uiMsg && <div className="vpe-hint">{uiMsg}</div>}
            </div>

            <div
              ref={this.trackRef}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={d}
              aria-valuenow={currentTime}
              tabIndex={0}
              onClick={this.onTrackClick}
              onMouseMove={this.onTrackMove}
              onMouseLeave={this.onTrackLeave}
              onKeyDown={this.onTrackKeyDown}
              className="vpe-track"
            >
              <div className="vpe-progress" style={{ width: `${this.timeToPct(currentTime)}%` }} />

              {segments.map((s) => {
                const left = this.timeToPct(Math.min(s.start, s.end));
                const width = this.timeToPct(Math.abs(s.end - s.start));
                const isActive = s.id === activeSegmentId;
                return (
                  <div
                    key={s.id}
                    className={`vpe-segment ${isActive ? "active" : ""}`}
                    style={{ left: `${left}%`, width: `${width}%` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      this.setState({
                        activeSegmentId: s.id,
                        segmentText: s.text || "",
                        uiMsg: null,
                      });
                    }}
                    title={`Segment ${this.format(s.start)} → ${this.format(s.end)}`}
                  />
                );
              })}

              {hoverTime !== null && (
                <div className="vpe-hoverTick" style={{ left: `${this.timeToPct(hoverTime)}%` }} />
              )}

              {segments.map((s) => (
                <React.Fragment key={`${s.id}_pins`}>
                  <div
                    className="vpe-pin"
                    style={{ left: `${this.timeToPct(s.start)}%` }}
                    title={`${this.format(s.start)} (start)`}
                    draggable
                    onDragStart={(e) => this.onPinDragStart(e, s.id, "start")}
                    onDrag={(e) => this.onPinDrag(e, s.id, "start")}
                    onDragEnd={(e) => this.onPinDragEnd(e, s.id, "start")}
                    onClick={(e) => this.onPinClick(e, s.id, "start")}
                  >
                    <span>⭐</span>
                  </div>
                  <div
                    className="vpe-pin"
                    style={{ left: `${this.timeToPct(s.end)}%` }}
                    title={`${this.format(s.end)} (end)`}
                    draggable
                    onDragStart={(e) => this.onPinDragStart(e, s.id, "end")}
                    onDrag={(e) => this.onPinDrag(e, s.id, "end")}
                    onDragEnd={(e) => this.onPinDragEnd(e, s.id, "end")}
                    onClick={(e) => this.onPinClick(e, s.id, "end")}
                  >
                    <span>🏁</span>
                  </div>
                </React.Fragment>
              ))}

              {this.state.pendingStartIndex !== null &&
                sortedPins[this.state.pendingStartIndex] && (
                  <div
                    className="vpe-pin vpe-pin--pending"
                    style={{
                      left: `${this.timeToPct(sortedPins[this.state.pendingStartIndex].time)}%`,
                    }}
                    title={`${this.format(sortedPins[this.state.pendingStartIndex].time)} (start)`}
                  >
                    <span>⭐</span>
                  </div>
                )}
            </div>
          </section>

          <aside className="vpe-side">
            <h3 className="vpe-sideTitle">Transcript</h3>
            {activeSegmentId ? (
              <textarea
                className="vpe-transcript"
                value={segmentText}
                onChange={(e) => {
                  const val = e.target.value;
                  this.setState((s) => ({
                    segmentText: val,
                    segments: s.segments.map((seg) =>
                      seg.id === s.activeSegmentId ? { ...seg, text: val } : seg
                    ),
                  }));
                }}
                placeholder="Transcript for the selected segment…"
              />
            ) : (
              <div className="vpe-empty">
                Click once to set ⭐ start, then again to set 🏁 end.
                <br />
                Click a colored segment to focus it.
              </div>
            )}

            <div className="vpe-segInfo">
              {activeSegmentId
                ? (() => {
                    const seg = this.state.segments.find((x) => x.id === activeSegmentId);
                    return seg
                      ? `Active: ${this.format(seg.start)} → ${this.format(seg.end)}`
                      : "";
                  })()
                : "No active segment."}
            </div>
          </aside>
        </div>
      </div>
    );
  }
}
